import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { movies, heroMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const heroData: Movie[] = heroMovies
    .map((id) => movies.find((m) => m.id === id))
    .filter((m): m is Movie => m !== undefined);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % heroData.length);
  }, [heroData.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const currentMovie = heroData[currentSlide];

  if (!currentMovie) return null;

  return (
    <section className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src={currentMovie.poster}
          alt={currentMovie.title}
          className="w-full h-full object-cover object-top"
        />
        {/* Gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-background-950/70 via-background-950/40 to-background-950"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/60 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full h-full flex items-center">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="max-w-xl">
            {/* Tags */}
            <div className="flex items-center gap-2 mb-4">
              {currentMovie.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium border border-primary-500/30 whitespace-nowrap"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-50 mb-4 leading-tight">
              {currentMovie.title}
            </h1>

            {/* Meta */}
            <div className="flex items-center gap-3 text-foreground-400 text-sm mb-6 flex-wrap">
              <span className="flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-primary-500"></i>
                </div>
                <span className="text-primary-400 font-bold">{currentMovie.rating}</span>
              </span>
              <span>{currentMovie.year}</span>
              <span className="text-foreground-600">|</span>
              <span>{currentMovie.category}</span>
              <span className="text-foreground-600">|</span>
              <span>{currentMovie.duration}</span>
              <span className="text-foreground-600">|</span>
              <span>{currentMovie.region}</span>
            </div>

            {/* Description */}
            <p className="text-foreground-400 text-sm leading-relaxed mb-8 line-clamp-2 max-w-md">
              {currentMovie.description}
            </p>

            {/* Buttons */}
            <div className="flex items-center gap-3">
              <Link
                to={`/article/${currentMovie.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-background-950 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-play-fill"></i>
                </div>
                立即观看
              </Link>
              <Link
                to={`/category/${currentMovie.categorySlug}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-background-700/60 hover:bg-background-600/60 text-foreground-200 font-medium rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap text-sm border border-background-600/50"
              >
                更多{currentMovie.category}片
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
        {heroData.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
              index === currentSlide
                ? 'w-8 bg-primary-500'
                : 'w-1.5 bg-foreground-500/40 hover:bg-foreground-400/60'
            }`}
            aria-label={`切换到第${index + 1}张`}
          />
        ))}
      </div>
    </section>
  );
}